import React from 'react';
import { Box, Skeleton, Typography, useTheme } from '@mui/material';
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  Tooltip,
  CartesianGrid,
  Cell,
  ResponsiveContainer,
} from 'recharts';

interface FunnelChartProps {
  titlesReceived: number;
  readingStarted: number;
  readingCompleted: number;
  loading: boolean;
}

const numberFormatter = new Intl.NumberFormat('pt-BR');

const pct = (value: number, base: number): string => {
  if (base === 0) return '0%';
  return `${Math.round((value / base) * 100)}%`;
};

const SCOPE_NOTE =
  'A conversão considera apenas entregas a usuários já cadastrados';

const FunnelChart: React.FC<FunnelChartProps> = ({
  titlesReceived,
  readingStarted,
  readingCompleted,
  loading,
}) => {
  const theme = useTheme();

  if (loading) {
    return <Skeleton variant="rectangular" width="100%" height={220} sx={{ borderRadius: 1 }} />;
  }

  const allZero = titlesReceived === 0 && readingStarted === 0 && readingCompleted === 0;

  if (allZero) {
    return (
      <Box>
        <Box
          sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: 220 }}
        >
          <Typography variant="body1" color="text.secondary">
            Sem dados de engajamento
          </Typography>
        </Box>
        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1 }}>
          * {SCOPE_NOTE}
        </Typography>
      </Box>
    );
  }

  const data = [
    {
      name: 'Títulos recebidos',
      value: titlesReceived,
      pct: '100%',
    },
    {
      name: 'Leitura iniciada',
      value: readingStarted,
      pct: pct(readingStarted, titlesReceived),
    },
    {
      name: 'Leitura finalizada',
      value: readingCompleted,
      pct: pct(readingCompleted, titlesReceived),
    },
  ];

  const colors = [
    theme.palette.primary.main,
    theme.palette.primary.light,
    theme.palette.secondary.main,
  ];

  const CustomTooltip = ({ active, payload }: any) => {
    if (active && payload && payload.length) {
      const item = payload[0].payload;
      return (
        <Box
          sx={{
            bgcolor: 'background.paper',
            border: 1,
            borderColor: 'divider',
            borderRadius: 1,
            p: 1.5,
          }}
        >
          <Typography variant="body2" sx={{ fontWeight: 600 }}>
            {item.name}
          </Typography>
          <Typography variant="body2">
            {numberFormatter.format(item.value)} ({item.pct})
          </Typography>
        </Box>
      );
    }
    return null;
  };

  return (
    <Box>
      <ResponsiveContainer width="100%" height={220}>
        <BarChart
          layout="vertical"
          data={data}
          margin={{ top: 8, right: 80, left: 130, bottom: 8 }}
        >
          <CartesianGrid strokeDasharray="3 3" stroke={theme.palette.divider} horizontal={false} />
          <XAxis
            type="number"
            tick={{ fontSize: 11 }}
            stroke={theme.palette.text.secondary}
            tickFormatter={(v: number) => numberFormatter.format(v)}
            scale="symlog"
            domain={[0, 'auto']}
          />
          <YAxis
            type="category"
            dataKey="name"
            tick={{ fontSize: 12 }}
            stroke={theme.palette.text.secondary}
            width={120}
          />
          <Tooltip content={<CustomTooltip />} />
          <Bar dataKey="value" radius={[0, 4, 4, 0]} label={{
            position: 'right',
            formatter: (val: any) => `${numberFormatter.format(Number(val))}`,
            fontSize: 12,
          }} minPointSize={5}>
            {data.map((_, index) => (
              <Cell key={`cell-${index}`} fill={colors[index % colors.length]} />
            ))}
          </Bar>
        </BarChart>
      </ResponsiveContainer>
      <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1 }}>
        * {SCOPE_NOTE}
      </Typography>
    </Box>
  );
};

export default FunnelChart;
